/**
 * src/shell/gm-client/hooks.ts — React 接线层（useSyncExternalStore 包引擎接口）。
 *
 * 引擎 index.ts 偏差登记：不提供 React hooks，外壳包一层——本文件即那层。
 */

import { useEffect, useState, useSyncExternalStore } from 'react'
import {
  replayEvents,
  subscribeEvents,
  type RunEvent,
  type RunStateInfo,
} from '../../gm'
import { gm } from './master'
import { getSnapshot, subscribeSnapshot, type SaveSnapshot } from './save-cache'
import { getToasts, subscribeToasts, type Toast } from './toast'

/** 存档快照（bundle + candidates），任何写批次后自动刷新 */
export function useSave(): SaveSnapshot {
  return useSyncExternalStore(subscribeSnapshot, getSnapshot)
}

/** 壳层提示条 */
export function useToasts(): Toast[] {
  return useSyncExternalStore(subscribeToasts, getToasts)
}

/**
 * 项目事件流（含回放）。引擎事件均为低频（量级百级），每次渲染直接重放；
 * 新事件 bump 重渲染 → 刷新页面后 replayEvents 从 localStorage 重建
 * ——回放可重建开发现场播报（验收项）。
 */
export function useProjectEvents(projectId: string | null): RunEvent[] {
  const [version, bump] = useState(0)
  useEffect(() => subscribeEvents(() => bump((n) => n + 1)), [])
  void version
  return replayEvents(projectId)
}

/** 全局事件段（封盘摘要等） */
export function useGlobalEvents(): RunEvent[] {
  const [version, bump] = useState(0)
  useEffect(() => subscribeEvents(() => bump((n) => n + 1)), [])
  void version
  return replayEvents(null)
}

/** 引擎运行状态（阶段灯/队列/会话进度），随事件流重算 */
export function useRunState(projectId: string | null): RunStateInfo | null {
  const [version, bump] = useState(0)
  useEffect(() => subscribeEvents(() => bump((n) => n + 1)), [])
  void version
  if (!projectId) return null
  try {
    return gm.getRunState(projectId)
  } catch {
    return null
  }
}
